import {
    ChangeEvent,
    Fragment,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";
import styled from "styled-components";
import { h } from "../../h";
import {
    ApiTokenInfo,
    createToken,
    listTokens,
    revokeToken,
} from "../../services/api";

// Personal access tokens for the 3rd-party API (CLI, MCP, custom tools).
// Lives in the logged-in drawer so Google-account users — who can't log
// in with a password anywhere else — can mint tokens with their session.

const LabelStyled = styled.div`
    font-size: 18px;
    color: ${({ theme }) => theme.colorMain};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 6px 11px;
    box-sizing: border-box;
`;

const HintStyled = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.colorMain};
    opacity: 0.7;
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 0 11px;
    box-sizing: border-box;
`;

const ErrorStyled = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.colorWarning};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 0 11px;
    box-sizing: border-box;
`;

const InputStyled = styled.input`
    width: 290px;
    height: 32px;
    margin: 10px 15px;
    outline: none;
    border: none;
    border-radius: 2px;
    padding-left: 11px;
    box-sizing: border-box;
    font-size: 18px;
    color: ${({ theme }) => theme.colorMain};
`;

const ButtonStyled = styled.button`
    cursor: pointer;
    background: none;
    outline: none;
    border: none;
    font-size: 18px;
    color: ${({ theme }) => theme.colorAccent};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 6px 11px;

    &:hover {
        opacity: 0.6;
    }
`;

const CheckboxLabelStyled = styled.label`
    font-size: 13px;
    color: ${({ theme }) => theme.colorMain};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 0 11px;
    box-sizing: border-box;
    display: block;
    cursor: pointer;
`;

const TokenRowStyled = styled.div`
    width: 290px;
    margin: 0 15px 0 0;
    padding: 6px 11px;
    box-sizing: border-box;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const TokenNameStyled = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.colorMain};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TokenDetailsStyled = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.colorMain};
    opacity: 0.7;
`;

const TokenRevokeStyled = styled.button`
    cursor: pointer;
    background: none;
    outline: none;
    border: none;
    font-size: 13px;
    color: ${({ theme }) => theme.colorWarning};
    flex: 0 0 auto;

    &:hover {
        opacity: 0.6;
    }
`;

export const ApiTokens = (): ReactElement => {
    const [tokens, setTokens] = useState<Array<ApiTokenInfo> | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState(``);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [newToken, setNewToken] = useState<string | null>(null);
    const [isCopied, setIsCopied] = useState(false);
    const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

    useEffect(() => {
        listTokens().then(setTokens, (loadError: Error) =>
            setError(loadError.message),
        );
    }, []);

    const create = useCallback(async () => {
        setError(null);
        setIsPending(true);
        try {
            const created = await createToken(
                name,
                isReadOnly ? `read` : `readwrite`,
            );
            setTokens((current) => [
                ...(current || []),
                {
                    id: created.id,
                    name: created.name,
                    scope: created.scope,
                    createdAt: created.createdAt,
                    lastUsedAt: created.lastUsedAt,
                },
            ]);
            setNewToken(created.token);
            setIsCopied(false);
            setName(``);
        } catch (createError) {
            setError(createError.message);
        } finally {
            setIsPending(false);
        }
    }, [name, isReadOnly]);

    const revoke = useCallback(
        async (id: string) => {
            if (pendingRevokeId !== id) {
                setPendingRevokeId(id);
                return;
            }
            setPendingRevokeId(null);
            setError(null);
            try {
                await revokeToken(id);
                setTokens(
                    (current) =>
                        current && current.filter((token) => token.id !== id),
                );
            } catch (revokeError) {
                setError(revokeError.message);
            }
        },
        [pendingRevokeId],
    );

    const copy = useCallback(() => {
        if (newToken) {
            navigator.clipboard.writeText(newToken).then(
                () => setIsCopied(true),
                () => undefined,
            );
        }
    }, [newToken]);

    return h(
        Fragment,
        {},
        h(LabelStyled, {}, `API tokens`),
        h(
            HintStyled,
            {},
            `For 3rd-party tools, the schd CLI, and AI assistants (MCP)`,
        ),
        error && h(ErrorStyled, {}, error),
        newToken &&
            h(
                Fragment,
                {},
                h(InputStyled, {
                    readOnly: true,
                    value: newToken,
                    onFocus: (event: ChangeEvent<HTMLInputElement>) =>
                        event.target.select(),
                }),
                h(HintStyled, {}, `Copy it now — it won't be shown again`),
                h(
                    ButtonStyled,
                    { onClick: copy },
                    isCopied ? `Copied` : `Copy token`,
                ),
            ),
        tokens &&
            tokens.map((token) =>
                h(
                    TokenRowStyled,
                    { key: token.id },
                    h(
                        TokenNameStyled,
                        {},
                        h(`div`, {}, token.name),
                        h(
                            TokenDetailsStyled,
                            {},
                            `${token.scope} · created ${token.createdAt.slice(
                                0,
                                10,
                            )}`,
                        ),
                    ),
                    h(
                        TokenRevokeStyled,
                        { onClick: () => revoke(token.id) },
                        pendingRevokeId === token.id ? `Sure?` : `Revoke`,
                    ),
                ),
            ),
        h(InputStyled, {
            placeholder: `New token name...`,
            "aria-label": `New token name`,
            value: name,
            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                setName(event.target.value),
        }),
        h(
            CheckboxLabelStyled,
            {},
            h(`input`, {
                type: `checkbox`,
                "aria-label": `Read-only token`,
                checked: isReadOnly,
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setIsReadOnly(event.target.checked),
            }),
            ` read-only`,
        ),
        h(
            ButtonStyled,
            {
                disabled: isPending || name.trim().length === 0,
                onClick: create,
            },
            `Create token`,
        ),
    );
};
